/**
 * Challenges feature constants.
 * Use UPPER_CASE for keys; no magic strings in components.
 */

import type { SectionBucket } from '../types/challenges.types';

/** Challenge status used to exclude from section buckets (never show in Finished/Active). */
export const CHALLENGE_STATUS_CANCELLED = 'CANCELLED';

/** Market ID for 1X2 / Match Winner (aligns with backend and create constants). */
export const MATCH_WINNER_MARKET_ID = 1;

/** Display label for 1X2 market in challenge cards and match rows. */
export const MATCH_WINNER_MARKET_NAME = 'Match Winner (1X2)';

/** Home/Draw/Away labels for 1X2 picks (shared by challenge cards and friend stats). */
export const PICK_OUTCOME_LABELS: Record<'home' | 'draw' | 'away', string> = {
  home: 'Home',
  draw: 'Draw',
  away: 'Away',
} as const;

/** Subtitle copy per section bucket; waitingForFriends is prefixed before displayName. Finished uses dynamic won/lost. */
export const SECTION_BUCKET_SUBTITLES: Record<SectionBucket | 'default', string> = {
  yourMove: 'Make Your Picks Now!',
  live: 'Tap to view live challenge',
  waitingForFriends: 'Waiting for',
  waitingForTaker: 'Waiting for a taker',
  waitingForOpponent: "Waiting for opponent's pick",
  active: 'Challenge Starts In',
  finished: 'Challenge completed', // Overridden per-card to "Challenge won" / "Challenge lost"
  default: 'Tap to view challenge matches',
};

/** Subtitle when first game has started (used for yourMove and active when countdown shows "Started"). */
export const CHALLENGE_STARTED_SUBTITLE = 'Challenge Started';

/** Finished challenge subtitle when current user won/lost/draw (used instead of SECTION_BUCKET_SUBTITLES.finished). */
export const FINISHED_SUBTITLES = {
  WON: 'Challenge won',
  LOST: 'Challenge lost',
  DRAW: 'Challenge draw',
} as const;

/** Fallback when pending 1v1 has no other participant in list (never show "You" as the card face). */
export const PENDING_1V1_NO_OTHER_LABEL = 'Waiting for response';

/** Finished group challenge subtitle format; use with rank: e.g. "Position #1". */
export const FINISHED_GROUP_POSITION_SUBTITLE = 'Position #%d';

/** Query param to open arena with a specific challenge expanded and scrolled into view (e.g. from notification). */
export const EXPAND_CHALLENGE_PARAM = 'expandChallenge';
/** `/app/arena?view=open` lands on the Open Board (Home's "Find a rival", specs/home-page AC-29). */
export const ARENA_VIEW_PARAM = 'view';
export const ARENA_VIEW_OPEN = 'open';

/** Approximate height (px) of list header (ArenaStatsBanner + filter bar) for scrollToIndex viewOffset so target lands at top. */
export const ARENA_LIST_HEADER_OFFSET = 150;

/**
 * Web-only top clearance for the Arena body. Matches `combinedHeader.minHeight` on web, which is
 * position:absolute there and therefore takes no flow space. Applied once on `arenaBody` so every
 * child (toggle, list, open board) clears the header instead of each one re-deriving it.
 */
export const ARENA_WEB_HEADER_CLEARANCE = 60;

/** Card face for an open challenge nobody has taken yet — there is no named opponent. */
export const OPEN_CHALLENGE_OPPONENT_LABEL = 'Anyone';

/**
 * Status filter for arena: includes PENDING, ACTIVE, and COMPLETED (for Finished tab).
 */
export const ARENA_STATUS_FILTER = 'PENDING,ACTIVE,COMPLETED';

/** Status filter for active list only (no history). */
export const ARENA_ACTIVE_STATUS = 'PENDING,ACTIVE';

/** Status filter for Finished (history) pagination. */
export const ARENA_HISTORY_STATUS = 'COMPLETED';

/** First page size for non-finished challenges (single-shot callers; see ARENA_ACTIVE_PAGE_SIZE for paginated). */
export const ARENA_ACTIVE_LIMIT = 100;

/** Per-request batch size when the Arena auto-paginates PENDING+ACTIVE. Matches CHALLENGES_LIST_MAX server-side. */
export const ARENA_ACTIVE_PAGE_SIZE = 100;

/**
 * Safety cap on auto-pagination of the Arena active query. With ARENA_ACTIVE_PAGE_SIZE=100
 * this allows up to 1000 PENDING+ACTIVE challenges per user before we stop walking pages
 * (anything beyond is almost certainly a runaway state worth investigating, not UX).
 */
export const ARENA_ACTIVE_MAX_PAGES = 10;

/** Limit for group leaderboard challenge fetch (include older group challenges). */
export const GROUP_LEADERBOARD_CHALLENGES_LIMIT = 100;

/**
 * Finished (history) pagination sizes.
 * GET challenges?status=COMPLETED returns the challenge list with picks[].match embedded;
 * the arena list builds match data from that (no separate bulk-matches call).
 */
/** Number of finished challenges to fetch on first load (Finished section). */
export const ARENA_HISTORY_INITIAL_PAGE_SIZE = 30;

/** Number of finished challenges to fetch on each "load more" (infinite scroll). */
export const ARENA_HISTORY_NEXT_PAGE_SIZE = 50;

/** Number of finished challenges to fetch for the preview strip in the All tab (parallel with active).
 *  Fetched as 6 (N+1 cursor pattern) so the hook can detect overflow without a separate count query. */
export const ARENA_HISTORY_PREVIEW_SIZE = 6;

/** Section header titles for flat list (keyed by SectionBucket). */
export const SECTION_TITLES: Record<SectionBucket, string> = {
  yourMove: 'Your Turn',
  live: 'LIVE',
  waitingForFriends: 'Waiting for Friends',
  waitingForTaker: 'Waiting for a Taker',
  waitingForOpponent: "Waiting for opponent's pick",
  active: 'Active',
  finished: 'Finished',
};

/** Badge pill per section for list headers (icon key maps to Lucide icon in ChallengesList). */
export type SectionBadgeVariant = 'error' | 'success' | 'warning' | 'neutral';
export const SECTION_BADGE_CONFIG: Record<
  SectionBucket,
  { badgeText: string; badgeVariant: SectionBadgeVariant; badgeIcon?: string; badgeShowChevron?: boolean }
> = {
  yourMove: { badgeText: 'Waiting for you', badgeVariant: 'error', badgeIcon: 'flash-on', badgeShowChevron: true },
  active: { badgeText: 'Good luck!', badgeVariant: 'success', badgeShowChevron: true },
  live: { badgeText: 'LIVE', badgeVariant: 'error', badgeIcon: 'flash-on' },
  waitingForFriends: { badgeText: 'PENDING', badgeVariant: 'warning', badgeIcon: 'hourglass-empty' },
  waitingForTaker: { badgeText: 'ON THE BOARD', badgeVariant: 'warning', badgeIcon: 'hourglass-empty' },
  waitingForOpponent: { badgeText: "Opponent's turn", badgeVariant: 'warning', badgeIcon: 'schedule' },
  finished: { badgeText: 'Completed', badgeVariant: 'neutral', badgeIcon: 'emoji-events', badgeShowChevron: true },
};

/** Badge/label copy for challenge card header */
export const CHALLENGE_HEADER_LABELS = {
  LIVE: 'LIVE',
  ON: 'ON',
  PENDING: 'PENDING',
  CREATOR: 'Creator',
  DECLINED: 'DECLINED',
  REMIND: 'Remind',
  CANCEL: 'Cancel',
} as const;

/** Max length for favorite pick name in arena banner (Match Winner (1X2) = 20). */
export const FAVORITE_BET_NAME_MAX_LENGTH = 20;

/** Arena stats banner labels (wins/draws/losses, favorite picks, streak). Wins and streak are 1v1-only. */
export const ARENA_STATS_LABELS = {
  WINS: '1v1 Wins',
  WIN_RATE: 'Win rate',
  ODDS_NET: 'Odds net',
  TOTAL_ODDS: 'Total odds',
  FAVORITE_BETS: 'Favorite picks',
  FAVORITE_BETS_SUBLABEL: 'most picked',
  STREAK: '1v1 Streak',
  STREAK_IN_A_ROW: 'in a row',
} as const;

/** Challenge card copy: participant switcher label (group challenges, expanded). */
export const CHALLENGE_CARD = {
  VIEW_PICKS_LABEL: 'Your pick vs',
} as const;

/** Default test ID for challenge card header */
export const CHALLENGE_CARD_HEADER_TEST_ID = 'challenge-card-header';

/** Full Arena empty state (no active or finished challenges): guest vs signed-in */
export const ARENA_EMPTY_STATE_COPY = {
  GUEST: {
    TITLE: 'Welcome to the Arena',
    SUBTITLE:
      'Sign up to create challenges, compete with friends, and climb the leaderboard.',
    CTA: 'Sign Up to Play',
  },
  SIGNED_IN: {
    TITLE: 'Your Arena is wide open',
    SUBTITLE:
      'Go make a challenge — pick your matches, invite friends, and see who nails the score.',
    CTA: 'Start a challenge',
  },
} as const;

/** Empty state when a filter has no results */
export const EMPTY_FILTER_SUBTITLE =
  'Nothing here for this filter. Try another tab or create a new challenge.';

/** Countdown timer labels for "Your Turn" cards */
export const COUNTDOWN_STARTED = 'Started';

/** Segment labels for digital countdown (days, hours, minutes) */
export const COUNTDOWN_SEGMENT_LABELS = {
  days: 'days',
  hours: 'hours',
  minutes: 'minutes',
} as const;

/** Your Turn card palette */
// TODO(theme): no near neighbors in src/theme/themes.ts — add tokens
//   theme.yourTurn.bg / theme.yourTurn.fg if these palette values need to
//   change between themes. Keeping literals for now to avoid invasive plumbing.
export const YOUR_TURN_HEADER_BG = '#023474';
export const YOUR_TURN_LABEL_COLOR = '#9C824A';

/** Status filter value for joinable group challenges (PENDING + ACTIVE). */
export const GROUP_CHALLENGES_STATUS_JOINABLE = 'PENDING,ACTIVE';

/** React Query staleTime for challenge queries (challenges stay fresh for 2 minutes). */
export const CHALLENGES_STALE_TIME_MS = 2 * 60 * 1000;

/** React Query gcTime for challenge queries (garbage collect after 10 minutes). */
export const CHALLENGES_GC_TIME_MS = 10 * 60 * 1000;

/** User-facing error strings returned by challenge card API calls. */
export const CHALLENGE_API_ERRORS = {
  CANCEL_FAILED: 'Failed to cancel challenge. Please try again.',
  REMIND_FAILED: 'Failed to send reminder. Please try again.',
  REMIND_COOLDOWN: 'Remind cooldown',
  DECLINE_FAILED: 'Failed to decline challenge. Please try again.',
  ACCEPT_FAILED: 'Failed to accept challenge. Please try again.',
  SUBMIT_PICKS_FAILED: 'Failed to submit picks. Please try again.',
  UPDATE_PREDICTIONS_FAILED: 'Failed to update predictions. Please try again.',
} as const;

/** Default cooldown (minutes) returned when server omits retryAfterMinutes on a 429 remind. */
export const REMIND_DEFAULT_COOLDOWN_MINUTES = 30;
